import { Link } from "react-router-dom";
import { Map, ArrowRight, ExternalLink } from "lucide-react";
import PageHeader from "../components/PageHeader";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import { COUNTRIES_DATA } from "../data/countriesData";
import { CITIES, BOARDS } from "../data/siteData";
import { AUTHORITY_PAGE_SLUGS } from "../data/authorityPagesData";
import { TUTOR_JOB_SLUGS } from "../data/tutorJobsData";
import { MAKEOVER_COUNTRIES, MAKEOVER_CATEGORIES, MAKEOVER_MASTER_PATH } from "../data/makeoverData";

/**
 * V66 — HTML sitemap (/sitemap).
 *
 * A human- and LLM-friendly link hub that mirrors the XML sitemap. LLMs
 * (ChatGPT, Claude, Perplexity, Gemini) rely heavily on visible <a href>
 * link hubs to discover related pages, so a well-organised HTML sitemap
 * meaningfully improves AI crawlability alongside Googlebot / Bingbot.
 *
 * Purely additive — no existing routes / forms / logic touched.
 */

const SITE_URL = "https://anandtutorials.com";

function SectionCard({ title, subtitle, children }) {
    return (
        <section className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#0F172A] mb-2">{title}</h2>
            {subtitle && <p className="text-slate-500 text-sm mb-5">{subtitle}</p>}
            {children}
        </section>
    );
}

function LinkList({ items }) {
    return (
        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-sm">
            {items.map(({ label, to, external }) => (
                <li key={to}>
                    <Link
                        to={to}
                        target={external ? "_blank" : undefined}
                        rel={external ? "noopener noreferrer" : undefined}
                        className="inline-flex items-center gap-1.5 text-[#1E3A8A] hover:text-amber-600 hover:gap-2 transition-all"
                    >
                        {label}
                        {external ? <ExternalLink className="w-3 h-3" /> : <ArrowRight className="w-3 h-3" />}
                    </Link>
                </li>
            ))}
        </ul>
    );
}

export default function HtmlSitemap() {
    // Static hub pages
    const brand = [
        { label: "Home", to: "/" },
        { label: "About Anand Tutorials", to: "/about" },
        { label: "Founding team", to: "/founder" },
        { label: "Our tutor team", to: "/team" },
        { label: "How it works", to: "/how-it-works" },
        { label: "Pricing (Fees & Plans)", to: "/pricing" },
        { label: "Contact us", to: "/contact" },
        { label: "FAQs (100+ answered)", to: "/faqs" },
        { label: "Blog & study strategies", to: "/blog" },
        { label: "Book a free 1:1 demo", to: "/register" },
        { label: "Pay Now", to: "/pay-now" },
    ];

    // Offline home tuition — Jabalpur / Bhopal / Nagpur only
    const offlineCities = Object.values(CITIES).map((c) => ({
        label: `Home tuition in ${c.name}`,
        to: `/${c.slug}`,
    }));

    // Offline city — keyword-slug aliases
    const cityAliases = [];
    ["bhopal", "jabalpur", "nagpur"].forEach((c) => {
        cityAliases.push(
            { label: `Home tuition ${c}`, to: `/home-tuition-${c}` },
            { label: `Home tutors ${c}`, to: `/home-tutors-${c}` },
            { label: `Online classes ${c}`, to: `/online-classes-${c}` },
            { label: `Anand Tutorials ${c}`, to: `/anand-tutorials-${c}` },
        );
    });

    // Online 1:1 — country hubs (27+ countries)
    const countries = Object.values(COUNTRIES_DATA).map((c) => ({
        label: `1:1 online tuition — ${c.flag} ${c.fullName}`,
        to: `/${c.urlSlug}`,
    }));

    // Boards
    const boards = BOARDS.map((b) => ({
        label: `${b.name} tuition`,
        to: `/courses/${b.slug || b.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`,
    }));

    // Authority landing pages (national + international pillars)
    const authority = AUTHORITY_PAGE_SLUGS.map((slug) => ({
        label: slug
            .replace(/-/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase())
            .replace(/\b(Cbse|Icse|Ib|Igcse|Neet|Jee|Ap|Sat|Uae|Uk|Usa|Nri|Api)\b/gi, (m) => m.toUpperCase()),
        to: `/${slug}`,
    }));

    // Tutor career pages
    const tutorCareers = [
        { label: "Become a tutor", to: "/become-a-tutor" },
        { label: "Tutor Guide (16-step onboarding)", to: "/tutor-guide" },
        { label: "Become-a-tutor FAQs", to: "/become-a-tutor-faqs" },
        ...TUTOR_JOB_SLUGS.map((slug) => ({
            label: slug
                .replace(/-/g, " ")
                .replace(/\b\w/g, (c) => c.toUpperCase()),
            to: `/${slug}`,
        })),
    ];

    // Policies
    const policies = [
        { label: "Terms of use", to: "/policies/terms" },
        { label: "Privacy policy", to: "/policies/privacy" },
        { label: "Refund policy", to: "/policies/refund" },
        { label: "Tutor policy", to: "/policies/tutor" },
        { label: "Parent policy", to: "/policies/parent" },
        { label: "Fee & payment policy", to: "/policies/fee-and-payment" },
        { label: "Gap & compensation policy", to: "/policies/gap-and-compensation" },
        { label: "Recontinuation policy", to: "/policies/recontinuation" },
    ];

    // Machine-readable manifests
    const manifests = [
        { label: "XML sitemap (sitemap.xml)", to: "/sitemap.xml", external: true },
        { label: "robots.txt", to: "/robots.txt", external: true },
        { label: "llms.txt (AI summary)", to: "/llms.txt", external: true },
        { label: "llms-full.txt (full corpus)", to: "/llms-full.txt", external: true },
        { label: "ai.txt (citation preferences)", to: "/ai.txt", external: true },
    ];

    // ItemList JSON-LD for AI answer engines
    const itemListSchema = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "@id": `${SITE_URL}/sitemap#itemlist`,
        "name": "Anand Tutorials — HTML Sitemap",
        "description": "Complete link index of every hub page on Anand Tutorials — human-readable and AI-crawlable.",
        "numberOfItems": brand.length + offlineCities.length + cityAliases.length + countries.length + boards.length + authority.length + tutorCareers.length + policies.length,
        "itemListElement": [
            ...brand,
            ...offlineCities,
            ...cityAliases,
            ...countries,
            ...boards,
            ...authority,
            ...tutorCareers,
            ...policies,
        ].slice(0, 200).map((it, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": it.label,
            "url": `${SITE_URL}${it.to}`,
        })),
    };

    const webPageSchema = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${SITE_URL}/sitemap#webpage`,
        "url": `${SITE_URL}/sitemap`,
        "name": "HTML Sitemap — Anand Tutorials",
        "description": "Complete human-readable and AI-crawlable index of all pages on anandtutorials.com.",
        "inLanguage": "en-IN",
        "isPartOf": { "@id": `${SITE_URL}/#organization` },
        "primaryImageOfPage": {
            "@type": "ImageObject",
            "url": `${SITE_URL}/logo-anand-tutorials.png`,
        },
    };

    return (
        <div data-testid="html-sitemap-page">
            <SEO
                title="HTML Sitemap — All Anand Tutorials pages in one place"
                description="Complete human-readable index of every page on Anand Tutorials — home tuition in Jabalpur, Bhopal, Nagpur, 1:1 live online classes across 27+ countries, boards, tutor careers, blog and policies. Machine-readable manifests included."
                keywords="Anand Tutorials sitemap, all pages, HTML sitemap, home tuition sitemap, online tuition sitemap, site index Anand Tutorials"
                path="/sitemap"
                schemas={[webPageSchema, itemListSchema]}
            />

            <Breadcrumbs items={[{ name: "Sitemap" }]} />

            <PageHeader
                eyebrow="Site index"
                title="HTML Sitemap"
                subtitle="A complete, human-readable index of every hub page on Anand Tutorials — the offline home-tuition cities, the 27+ online-tuition country hubs, boards, tutor-career pages, blogs, and policies."
            >
                <div className="flex flex-wrap gap-3">
                    <a href="/sitemap.xml" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-5 py-3 rounded-lg hover:bg-amber-400 transition-colors">
                        <Map className="w-4 h-4" /> XML sitemap
                    </a>
                    <a href="/llms.txt" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur text-white font-semibold px-5 py-3 rounded-lg hover:bg-white/20 transition-colors">
                        <ExternalLink className="w-4 h-4" /> llms.txt (AI manifest)
                    </a>
                </div>
            </PageHeader>

            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14 space-y-8">

                <SectionCard title="Brand · About · Contact" subtitle="Core pages every visitor should see first.">
                    <LinkList items={brand} />
                </SectionCard>

                <SectionCard
                    title="Offline home tuition — cities served"
                    subtitle="In-person home tuition is available strictly in these three cities. Everywhere else, we deliver live 1:1 online classes."
                >
                    <LinkList items={offlineCities} />
                    <div className="mt-6 pt-6 border-t border-slate-100">
                        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-3">Keyword-slug aliases (same content, keyword-exact URLs)</div>
                        <LinkList items={cityAliases} />
                    </div>
                </SectionCard>

                <SectionCard
                    title="Online 1:1 live classes — countries served"
                    subtitle="27+ dedicated country hubs. Same panel of Cambridge / Oxford / IIT / AIIMS alumni tutors, tuned to each country's timezone."
                >
                    <LinkList items={countries} />
                    <div className="mt-4">
                        <Link to="/online-classes" className="inline-flex items-center gap-1.5 text-sm text-[#1E3A8A] hover:text-amber-600 font-semibold">
                            Pan-India online-classes hub <ArrowRight className="w-4 h-4" />
                        </Link>
                        <span className="mx-3 text-slate-300">·</span>
                        <Link to="/online-tuition-worldwide" className="inline-flex items-center gap-1.5 text-sm text-[#1E3A8A] hover:text-amber-600 font-semibold">
                            Worldwide index <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </SectionCard>

                <SectionCard title="Boards & curricula" subtitle="Dedicated pages for every board Anand Tutorials teaches.">
                    <LinkList items={boards} />
                </SectionCard>

                <SectionCard
                    title="Authority hubs & topic clusters"
                    subtitle="National + international pillar pages built for high-intent search."
                >
                    <LinkList items={authority} />
                </SectionCard>

                <SectionCard title="Tutor careers" subtitle="How to apply, tutor guide, and city-specific tutor-job pages.">
                    <LinkList items={tutorCareers} />
                </SectionCard>

                {/* V77 — 1-on-1 Live Online Makeover Classes */}
                <SectionCard title="1-on-1 Live Online Makeover Classes" subtitle="Grooming, styling, image consulting and personality coaching — worldwide + Pan India.">
                    <LinkList items={[
                        { label: "Master — 1-on-1 Live Online Makeover Classes", to: MAKEOVER_MASTER_PATH },
                        ...MAKEOVER_COUNTRIES.map((c) => ({
                            label: `${c.flag} 1-on-1 Live Online Makeover Classes in ${c.name}`,
                            to: `${MAKEOVER_MASTER_PATH}-${c.slug}`,
                        })),
                        ...MAKEOVER_CATEGORIES.map((c) => ({
                            label: `${c.name} — 1-on-1 Live Online Classes`,
                            to: `${MAKEOVER_MASTER_PATH}/${c.slug}`,
                        })),
                    ]} />
                </SectionCard>

                <SectionCard title="Policies" subtitle="Every policy governing our tuition, tutors and payments.">
                    <LinkList items={policies} />
                </SectionCard>

                <SectionCard
                    title="Machine-readable manifests"
                    subtitle="For crawlers and AI answer engines. All served with correct MIME types."
                >
                    <LinkList items={manifests} />
                </SectionCard>

            </div>
        </div>
    );
}
